'use client';

import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Database, Download, RefreshCw, Search, ShieldAlert } from 'lucide-react';
import { Header } from '@/components/header';
import { Sidebar } from '@/components/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getAccessGuardMetrics,
  type AccessGuardMetricsEntry,
  type MetricsStorageUsage,
} from '@/lib/urm-api';
import { cn } from '@/lib/utils';
import {
  buildAccessGuardRows,
  aggregateAccessGuardRowsByUser,
  accessGuardRowsToCsv,
  accessGuardUserSummariesToCsv,
  sortAccessGuardRows,
  sortAccessGuardUserSummaries,
  type AccessGuardSortField,
} from './logic';

const PAGE_LIMIT = 1000;
const MAX_PAGES = 100;
type ViewMode = 'limiters' | 'users';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toLocaleString()} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[unitIndex]}`;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export default function AccessGuardPage() {
  const [entries, setEntries] = useState<AccessGuardMetricsEntry[]>([]);
  const [storage, setStorage] = useState<MetricsStorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [action, setAction] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [resourceType, setResourceType] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('limiters');
  const [sortField, setSortField] = useState<AccessGuardSortField>('count');
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const runId = ++runIdRef.current;
    const isCurrent = () => runIdRef.current === runId && !controller.signal.aborted;

    setLoading(true);
    setError(null);
    try {
      const collected: AccessGuardMetricsEntry[] = [];
      let page = await getAccessGuardMetrics(
        { limit: PAGE_LIMIT, includeStorage: true },
        controller.signal,
      );
      collected.push(...page.entries);
      const nextStorage = page.storage ?? null;
      let cursor = page.next_cursor;
      let pageCount = 1;

      while (page.truncated) {
        if (!cursor) throw new Error('AccessGuard pagination stopped without a cursor');
        if (pageCount >= MAX_PAGES) throw new Error('AccessGuard response exceeded the page limit');
        const previousCursor = cursor;
        page = await getAccessGuardMetrics(
          { limit: PAGE_LIMIT, startAfter: cursor },
          controller.signal,
        );
        collected.push(...page.entries);
        cursor = page.next_cursor;
        pageCount += 1;
        if (page.truncated && cursor === previousCursor) {
          throw new Error('AccessGuard pagination cursor did not advance');
        }
      }

      if (!isCurrent()) return;
      setEntries(collected);
      setStorage(nextStorage);
      setLastUpdatedMs(Date.now());
    } catch (caught: unknown) {
      if (!isCurrent() || isAbortError(caught)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => { void load(); });
    return () => {
      runIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, [load]);

  const actions = useMemo(
    () => Array.from(new Set(entries.map((entry) => entry.action))).sort(),
    [entries],
  );
  const rows = useMemo(() => buildAccessGuardRows(entries), [entries]);
  const resourceTypes = useMemo(
    () => Array.from(new Set(rows.flatMap((row) =>
      row.dimensions.type ? [row.dimensions.type] : [],
    ))).sort(),
    [rows],
  );
  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    const normalizedUser = userFilter.trim().toLocaleLowerCase();
    return rows.filter((entry) =>
      (action === 'all' || entry.action === action)
      && (viewMode === 'users' || resourceType === 'all' || entry.dimensions.type === resourceType)
      && (!normalizedUser || entry.dimensions.user?.toLocaleLowerCase().includes(normalizedUser))
      && (!normalized
        || entry.action.toLocaleLowerCase().includes(normalized)
        || entry.dimensions.user?.toLocaleLowerCase().includes(normalized)
        || (viewMode === 'limiters' && (
          entry.limiter_key.toLocaleLowerCase().includes(normalized)
          || entry.dimensions.type?.toLocaleLowerCase().includes(normalized)
          || entry.dimensions.name?.toLocaleLowerCase().includes(normalized)
        ))),
    );
  }, [action, query, resourceType, rows, userFilter, viewMode]);
  const visibleEntries = useMemo(
    () => sortAccessGuardRows(filteredRows, sortField, sortField === 'count' ? 'desc' : 'asc'),
    [filteredRows, sortField],
  );
  const visibleUserSummaries = useMemo(
    () => sortAccessGuardUserSummaries(
      aggregateAccessGuardRowsByUser(filteredRows),
      sortField === 'type' || sortField === 'name' ? 'count' : sortField,
      sortField === 'count' ? 'desc' : 'asc',
    ),
    [filteredRows, sortField],
  );
  const totalViolations = useMemo(
    () => entries.reduce((total, entry) => total + entry.count, 0),
    [entries],
  );

  const exportVisibleRows = () => {
    const aggregateByUser = viewMode === 'users';
    const csv = aggregateByUser
      ? accessGuardUserSummariesToCsv(visibleUserSummaries)
      : accessGuardRowsToCsv(visibleEntries);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const viewSuffix = aggregateByUser ? '-by-user' : '';
    link.download = `access-guard-violations${viewSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const visibleCount = viewMode === 'users' ? visibleUserSummaries.length : visibleEntries.length;

  const changeViewMode = (nextMode: ViewMode) => {
    setViewMode(nextMode);
    if (nextMode === 'users' && (sortField === 'type' || sortField === 'name')) {
      setSortField('count');
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-7 w-7 text-destructive" aria-hidden="true" />
                  <h1 className="text-3xl font-bold">Access Guard Violations</h1>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Limit-reached events retained in the live AccessGuard metrics store.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportVisibleRows} disabled={visibleCount === 0}>
                  <Download aria-hidden="true" />
                  Export CSV
                </Button>
                <Button variant="outline" onClick={() => void load()} disabled={loading}>
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} aria-hidden="true" />
                  {loading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard title="Limit reached" value={totalViolations.toLocaleString()} icon={AlertTriangle} />
              <SummaryCard title="Affected limiters" value={entries.length.toLocaleString()} icon={ShieldAlert} />
              <SummaryCard title="Actions" value={actions.length.toLocaleString()} icon={ShieldAlert} />
              <SummaryCard
                title="Redis storage"
                value={storage ? `${storage.approximate ? '≈' : ''}${formatBytes(storage.redis_memory_bytes)}` : 'Unavailable'}
                detail={storage ? `${storage.redis_key_count.toLocaleString()} retained daily keys` : undefined}
                icon={Database}
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              A count is recorded when a limiter reaches its configured threshold, not for every request during cooldown.
              Daily buckets are retained for three days, and limiter keys contain sanitized usernames.
            </div>

            {error && (
              <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                <p className="font-semibold">Could not load AccessGuard violations</p>
                <p className="mt-1">{error}</p>
              </div>
            )}

            <Card className="overflow-hidden shadow-sm">
              <CardHeader className="space-y-0 border-b p-0">
                <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-5">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <CardTitle className="text-lg">Violations</CardTitle>
                      <Badge variant="secondary" className="font-medium tabular-nums">
                        {visibleCount.toLocaleString()} shown
                      </Badge>
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {viewMode === 'users'
                        ? 'Grouped by user and AccessGuard bucket'
                        : `${entries.length.toLocaleString()} retained limiter records`}
                    </p>
                  </div>
                  {lastUpdatedMs && (
                    <p className="rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                      Updated {new Date(lastUpdatedMs).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <div className="bg-muted/25 px-6 py-4">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <label className="space-y-1.5">
                    <span className="block text-xs font-medium text-muted-foreground">View</span>
                    <select
                      aria-label="Violation view"
                      value={viewMode}
                      onChange={(event) => changeViewMode(event.target.value as ViewMode)}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                    >
                      <option value="limiters">Individual limiters</option>
                      <option value="users">Aggregate by user</option>
                    </select>
                  </label>
                  <label className="space-y-1.5 xl:col-span-2">
                    <span className="block text-xs font-medium text-muted-foreground">Search</span>
                    <span className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      <input
                        type="search"
                        aria-label="Search violations"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={viewMode === 'users' ? 'User or bucket…' : 'Resource, action, or limiter key…'}
                        className="h-10 w-full rounded-md border bg-background pl-9 pr-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                      />
                    </span>
                  </label>
                  <label className="space-y-1.5">
                    <span className="block text-xs font-medium text-muted-foreground">User</span>
                    <input
                      aria-label="Filter by user"
                      value={userFilter}
                      onChange={(event) => setUserFilter(event.target.value)}
                      placeholder="All users"
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                    />
                  </label>
                  <label className="space-y-1.5">
                    <span className="block text-xs font-medium text-muted-foreground">Action</span>
                    <select
                      aria-label="Filter by action"
                      value={action}
                      onChange={(event) => setAction(event.target.value)}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All actions</option>
                      {actions.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                  </label>
                  {viewMode === 'limiters' && (
                    <label className="space-y-1.5">
                      <span className="block text-xs font-medium text-muted-foreground">Resource type</span>
                      <select
                        aria-label="Filter by resource type"
                        value={resourceType}
                        onChange={(event) => setResourceType(event.target.value)}
                        className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                      >
                        <option value="all">All resource types</option>
                        {resourceTypes.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="space-y-1.5">
                    <span className="block text-xs font-medium text-muted-foreground">Sort by</span>
                    <select
                      aria-label="Sort violations by"
                      value={sortField}
                      onChange={(event) => setSortField(event.target.value as AccessGuardSortField)}
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-ring"
                    >
                      <option value="count">Count (highest first)</option>
                      <option value="user">User (A–Z)</option>
                      <option value="action">Action (A–Z)</option>
                      {viewMode === 'limiters' && <option value="type">Resource type (A–Z)</option>}
                      {viewMode === 'limiters' && <option value="name">Resource name (A–Z)</option>}
                    </select>
                  </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto" aria-busy={loading}>
                  {viewMode === 'users' ? (
                    <table className="w-full text-left text-sm">
                      <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-6 py-3.5 font-medium">User</th>
                          <th className="px-6 py-3.5 font-medium">Bucket</th>
                          <th className="px-6 py-3.5 text-right font-medium">Violations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUserSummaries.map((summary) => (
                          <tr key={`${summary.user ?? ''}:${summary.action}`} className="border-b transition-colors last:border-0 hover:bg-muted/30">
                            <td className="max-w-xl break-all px-6 py-4 text-sm font-medium">
                              {summary.user ?? 'Resource-scoped (no user)'}
                            </td>
                            <td className="px-6 py-4"><Badge variant="outline">{summary.action}</Badge></td>
                            <td className="px-6 py-4 text-right text-base font-semibold tabular-nums">
                              {summary.count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-6 py-3.5 font-medium">Action</th>
                        <th className="px-6 py-3.5 font-medium">User</th>
                        <th className="px-6 py-3.5 font-medium">Resource</th>
                        <th className="px-6 py-3.5 text-right font-medium">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEntries.map((entry) => (
                        <tr key={`${entry.action}:${entry.limiter_key}`} className="border-b transition-colors last:border-0 hover:bg-muted/30">
                          <td className="px-6 py-4"><Badge variant="outline">{entry.action}</Badge></td>
                          <td className="max-w-64 break-all px-6 py-4 text-sm font-medium">
                            {entry.dimensions.user ?? '—'}
                          </td>
                          <td className="max-w-2xl px-6 py-4">
                            {entry.dimensions.recognized ? (
                              entry.dimensions.type ? (
                                <>
                                  <p className="font-semibold text-foreground">{entry.dimensions.type} / {entry.dimensions.name}</p>
                                  <p className="mt-0.5 break-all font-mono text-xs text-muted-foreground">
                                    {entry.dimensions.group}/{entry.dimensions.version} · {entry.dimensions.path}
                                  </p>
                                </>
                              ) : <span className="text-muted-foreground">All resources</span>
                            ) : (
                              <>
                                <p className="text-xs font-medium text-amber-700">Unparsed limiter key</p>
                                <p className="break-all font-mono text-xs">{entry.limiter_key}</p>
                              </>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right text-base font-semibold tabular-nums">{entry.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                  {!loading && visibleCount === 0 && (
                    <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                      {entries.length === 0 ? 'No AccessGuard violations are currently retained.' : 'No violations match the filters.'}
                    </div>
                  )}
                  {loading && entries.length === 0 && (
                    <div role="status" className="px-6 py-12 text-center text-sm text-muted-foreground">
                      Loading AccessGuard violations…
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  detail,
  icon: Icon,
}: {
  title: string;
  value: string;
  detail?: string;
  icon: typeof ShieldAlert;
}) {
  return (
    <Card className="shadow-sm">
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
          {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
        </div>
        <span className="rounded-lg bg-primary/10 p-2.5 text-primary">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </CardContent>
    </Card>
  );
}
